export const copilotReviewEffortPickerSelector =
  '[data-copilot-review-effort-picker][data-copilot-review-effort-version="1"]'

export const copilotReviewEffortFieldNames = [
  'review_effort_level',
  'review_effort_level_selected',
  'review_effort_snapshot',
  'review_effort_client_version',
] as const

export type CopilotReviewEffortFields = {
  review_effort_level?: string
  review_effort_level_selected?: boolean | string
  review_effort_snapshot?: string
  review_effort_client_version?: string
}

type CopilotReviewEffortResponse = {
  review_effort_level: string
  review_effort_level_selected: boolean
  review_effort_snapshot: string
  review_effort_client_version: ''
}

export type CopilotReviewEffortSubmission = {
  readonly picker: Element
  selectionRevision: number
  responseRevision: number
  context: string
  documentBody: HTMLElement
  fields: CopilotReviewEffortFields
  state: PickerState
}

type PickerState = {
  picker: Element
  selectionRevision: number
  responseRevision: number
}

const pickerStates = new WeakMap<Element, PickerState>()

function getPickerState(picker: Element) {
  let state = pickerStates.get(picker)
  if (!state || state.picker !== picker) {
    state = {picker, selectionRevision: 0, responseRevision: 0}
    pickerStates.set(picker, state)
  }
  return state
}

function findSupportedPicker(root: Element | null) {
  if (root?.matches(copilotReviewEffortPickerSelector)) return root
  return root?.querySelector(copilotReviewEffortPickerSelector)
}

export function collectCopilotReviewEffort(picker: Element | null): CopilotReviewEffortFields | undefined {
  if (!picker) return

  const supported = findSupportedPicker(picker)
  const root = supported ?? picker
  const effort = root.querySelector<HTMLInputElement>('input[name="review_effort_level"]')?.value
  if (!supported) return effort ? {review_effort_level: effort} : undefined

  const selected = root.querySelector<HTMLInputElement>('input[name="review_effort_level_selected"]')?.value
  return {
    review_effort_level: effort,
    review_effort_level_selected: selected === 'true' ? true : selected === 'false' ? false : selected,
    review_effort_snapshot: root.querySelector<HTMLInputElement>('input[name="review_effort_snapshot"]')?.value,
    review_effort_client_version: root.querySelector<HTMLInputElement>('input[name="review_effort_client_version"]')
      ?.value,
  }
}

export function getCopilotReviewEffortContext(picker: Element, form = picker.closest('form')) {
  const location = picker.ownerDocument.location
  return JSON.stringify([
    location.origin,
    location.pathname,
    location.search,
    picker.ownerDocument.head.querySelector('meta[name="user-login"]')?.getAttribute('content'),
    form?.getAttribute('id'),
    form?.getAttribute('action'),
    picker.closest('.js-issue-sidebar-form[data-url]')?.getAttribute('data-url'),
  ])
}

function setField(picker: Element, name: (typeof copilotReviewEffortFieldNames)[number], value: string | boolean) {
  let input = picker.querySelector<HTMLInputElement>(`input[name="${name}"]`)
  if (!input) {
    input = picker.ownerDocument.createElement('input')
    input.type = 'hidden'
    input.name = name
    const container =
      (name === 'review_effort_level' ? picker.querySelector('action-menu [data-list-inputs="true"]') : null) ?? picker
    container.append(input)
  }
  input.value = String(value)
}

export function enhanceCopilotReviewEffortPicker(picker: Element) {
  if (picker.matches(copilotReviewEffortPickerSelector)) setField(picker, 'review_effort_client_version', '1')
}

function hasCopilotReviewEffortFields(
  fields: CopilotReviewEffortFields | undefined,
): fields is Required<CopilotReviewEffortFields> {
  return fields !== undefined && copilotReviewEffortFieldNames.every(name => fields[name] !== undefined)
}

function writeCopilotReviewEffortFields(picker: Element, fields: Required<CopilotReviewEffortFields>) {
  const previousEffort = collectCopilotReviewEffort(picker)?.review_effort_level
  for (const name of copilotReviewEffortFieldNames) setField(picker, name, fields[name])
  enhanceCopilotReviewEffortPicker(picker)

  if (previousEffort !== fields.review_effort_level) {
    for (const item of picker.querySelectorAll('[role="menuitemradio"][data-value]')) {
      item.setAttribute('aria-checked', String(item.getAttribute('data-value') === fields.review_effort_level))
    }
    const menu = picker.querySelector('action-menu')
    if (menu) {
      const restoreFocus = menu.contains(picker.ownerDocument.activeElement)
      const replacement = menu.cloneNode(true)
      menu.replaceWith(replacement)
      if (restoreFocus && replacement instanceof Element) {
        replacement.querySelector<HTMLElement>('button[aria-controls]')?.focus()
      }
    }
  }
}

export function preserveCopilotReviewEffortState(
  previous: Element,
  replacement: Element,
): 'incomplete' | 'preserved' | undefined {
  if (!previous.matches(copilotReviewEffortPickerSelector) || !replacement.matches(copilotReviewEffortPickerSelector)) {
    return
  }
  const previousState = pickerStates.get(previous)
  if (previousState && previousState.picker !== previous) return
  const fields = collectCopilotReviewEffort(previous)
  if (!hasCopilotReviewEffortFields(fields)) return 'incomplete'

  const state = previousState ?? getPickerState(previous)
  const replacementState = pickerStates.get(replacement)
  if (
    replacementState &&
    replacementState !== state &&
    (replacementState.selectionRevision > 0 || replacementState.responseRevision > 0)
  ) {
    return
  }
  writeCopilotReviewEffortFields(replacement, fields)
  state.picker = replacement
  pickerStates.set(replacement, state)
  return 'preserved'
}

export function recordCopilotReviewEffortSelection(picker: Element, item: HTMLElement) {
  if (!picker.contains(item) || item.querySelector('[role^="menuitem"]')?.matches(':disabled, [aria-disabled]')) {
    return
  }
  getPickerState(picker).selectionRevision += 1
  const marker = picker.querySelector<HTMLInputElement>('input[name="review_effort_level_selected"]')
  if (marker) marker.value = 'true'
}

export function captureCopilotReviewEffort(picker: Element | null): CopilotReviewEffortSubmission | undefined {
  const root = findSupportedPicker(picker)
  if (!root?.isConnected) return
  const fields = collectCopilotReviewEffort(root)
  if (!fields || fields.review_effort_client_version !== '1') return
  const state = getPickerState(root)
  return {
    get picker() {
      return state.picker
    },
    selectionRevision: state.selectionRevision,
    responseRevision: state.responseRevision,
    context: getCopilotReviewEffortContext(root),
    documentBody: root.ownerDocument.body,
    fields,
    state,
  }
}

export function isCurrentCopilotReviewEffort(submission: CopilotReviewEffortSubmission) {
  return (
    isRelatedCopilotReviewEffort(submission) &&
    submission.state.responseRevision === submission.responseRevision &&
    submission.state.selectionRevision === submission.selectionRevision
  )
}

export function isRelatedCopilotReviewEffort(submission: CopilotReviewEffortSubmission) {
  const {picker, context, documentBody, state} = submission
  return (
    picker.isConnected &&
    picker.matches(copilotReviewEffortPickerSelector) &&
    picker.ownerDocument.body === documentBody &&
    getCopilotReviewEffortContext(picker) === context &&
    pickerStates.get(picker) === state
  )
}

export function hasNewerCopilotReviewEffortChoice(submission: CopilotReviewEffortSubmission) {
  return submission.state.selectionRevision !== submission.selectionRevision
}

export function changesCopilotReviewEffortSubmission(
  submission: CopilotReviewEffortSubmission,
  fields: CopilotReviewEffortResponse,
) {
  return (
    fields.review_effort_level !== submission.fields.review_effort_level ||
    fields.review_effort_level_selected !== submission.fields.review_effort_level_selected ||
    fields.review_effort_snapshot !== submission.fields.review_effort_snapshot
  )
}

export function updateCopilotReviewEffortResponseState(
  submission: CopilotReviewEffortSubmission,
  replacement: Element,
  fields: CopilotReviewEffortResponse,
) {
  const {state} = submission
  if (changesCopilotReviewEffortSubmission(submission, fields)) state.responseRevision += 1
  state.picker = replacement
  pickerStates.set(replacement, state)
}

export function isCopilotReviewEffortResponse(value: unknown): value is CopilotReviewEffortResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'review_effort_level' in value &&
    typeof value.review_effort_level === 'string' &&
    value.review_effort_level.length > 0 &&
    'review_effort_level_selected' in value &&
    typeof value.review_effort_level_selected === 'boolean' &&
    'review_effort_snapshot' in value &&
    typeof value.review_effort_snapshot === 'string' &&
    value.review_effort_snapshot.length > 0 &&
    'review_effort_client_version' in value &&
    value.review_effort_client_version === ''
  )
}

export function applyCopilotReviewEffortResponse(
  submission: CopilotReviewEffortSubmission | undefined,
  response: unknown,
) {
  if (
    !submission ||
    !isCurrentCopilotReviewEffort(submission) ||
    !isCopilotReviewEffortResponse(response) ||
    response.review_effort_level_selected
  ) {
    return
  }

  const {picker} = submission
  submission.state.responseRevision += 1
  writeCopilotReviewEffortFields(picker, response)
}
